import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { MAX_ATTACHMENTS } from "../lib/attachments";
import { PaperclipIcon } from "./icons";

/** True when a drag carries files (vs. text / element drags). */
function hasFiles(event: DragEvent): boolean {
  return Array.from(event.dataTransfer?.types ?? []).includes("Files");
}

/**
 * Page-wide file drop: drop files anywhere on the chat surface to attach them.
 * Listens at the window so the whole page is a target, tracks drag depth so
 * moving across child elements doesn't flicker the overlay, and only reacts to
 * file drags. The dashed overlay is a portal so it floats above everything.
 */
export function PageFileDrop({ onFiles }: { onFiles: (files: File[]) => void }) {
  const { t } = useTranslation();
  const [active, setActive] = useState(false);
  const depth = useRef(0);

  useEffect(() => {
    function onEnter(event: DragEvent) {
      if (!hasFiles(event)) return;
      event.preventDefault();
      depth.current += 1;
      setActive(true);
    }
    function onOver(event: DragEvent) {
      if (hasFiles(event)) event.preventDefault();
    }
    function onLeave(event: DragEvent) {
      if (!hasFiles(event)) return;
      depth.current -= 1;
      if (depth.current <= 0) {
        depth.current = 0;
        setActive(false);
      }
    }
    function onDrop(event: DragEvent) {
      if (!hasFiles(event)) return;
      event.preventDefault();
      depth.current = 0;
      setActive(false);
      const files = Array.from(event.dataTransfer?.files ?? []);
      if (files.length) onFiles(files);
    }
    window.addEventListener("dragenter", onEnter);
    window.addEventListener("dragover", onOver);
    window.addEventListener("dragleave", onLeave);
    window.addEventListener("drop", onDrop);
    return () => {
      window.removeEventListener("dragenter", onEnter);
      window.removeEventListener("dragover", onOver);
      window.removeEventListener("dragleave", onLeave);
      window.removeEventListener("drop", onDrop);
    };
  }, [onFiles]);

  if (!active) return null;

  return createPortal(
    <div className="pointer-events-none fixed inset-0 z-[60] flex items-center justify-center bg-canvas/70 p-6 backdrop-blur-[2px]">
      <div className="flex flex-col items-center gap-3 rounded-2xl border-2 border-dashed border-ink/40 bg-paper/85 px-12 py-9 text-center shadow-[var(--shadow-pill)]">
        <PaperclipIcon className="size-7 text-ink-soft" />
        <span className="font-sans text-[15px] font-medium text-ink">
          {t("chat.attachments.dropTitle")}
        </span>
        <span className="font-sans text-[13px] text-ink-soft">
          {t("chat.attachments.dropHint", { max: MAX_ATTACHMENTS })}
        </span>
      </div>
    </div>,
    document.body,
  );
}
